import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useHttpClient } from "@/providers/HttpClient";
import { robotoHeaders } from "@/shared/http";
import { LoggerService } from "@/shared/services/LoggerService";

import styles from "./KnowledgeGraph.module.css";

interface ExtractionResult {
  enqueued: boolean;
  max_threads: number;
}

interface GraphStats {
  org_id: string;
  total_nodes: number;
  total_edges: number;
  memory_nodes: number;
  kinds: Record<string, number>;
}

export function KnowledgeGraph() {
  const defaultOrgId = useCurrentOrgId();
  const { httpClient } = useHttpClient();

  const [orgId, setOrgId] = React.useState(defaultOrgId ?? "");
  const [maxThreads, setMaxThreads] = React.useState(50);
  const [isExtracting, setIsExtracting] = React.useState(false);
  const [isLoadingStats, setIsLoadingStats] = React.useState(false);
  const [isClearing, setIsClearing] = React.useState(false);
  const [result, setResult] = React.useState<ExtractionResult | null>(null);
  const [stats, setStats] = React.useState<GraphStats | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const fetchStats = React.useCallback(async () => {
    if (!orgId.trim()) {
      return;
    }
    setIsLoadingStats(true);
    setError(null);
    try {
      const url = httpClient.constructUrl(
        `v1/knowledge-graph/orgs/${orgId}/stats`,
      );
      const response = await httpClient.get(url, {
        headers: robotoHeaders({ orgId }),
      });
      const data = await response.json<GraphStats>();
      setStats(data);
    } catch (err) {
      LoggerService.error("Failed to fetch graph stats", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch graph stats",
      );
    } finally {
      setIsLoadingStats(false);
    }
  }, [httpClient, orgId]);

  const handleClear = async () => {
    if (!orgId.trim()) {
      setError("Org ID is required");
      return;
    }

    const confirmed = window.confirm(
      `This will permanently delete ALL nodes and relationships in the knowledge graph for org "${orgId}". This cannot be undone. Continue?`,
    );
    if (!confirmed) {
      return;
    }

    setIsClearing(true);
    setError(null);

    try {
      const url = httpClient.constructUrl(
        `v1/knowledge-graph/orgs/${orgId}/graph`,
      );
      const response = await httpClient.delete(url, {
        // The backend requires the org id echoed back in the body: an irreversible DETACH DELETE
        // keyed only on a URL segment is one fat-fingered request away from wiping the wrong org,
        // so the confirm token makes that mistake need two matching inputs. Omitting it made every
        // click a 400.
        body: JSON.stringify({ confirm: orgId }),
        headers: robotoHeaders({ orgId }),
        idempotent: false,
      });
      await response.json();
      void fetchStats();
    } catch (err) {
      LoggerService.error("Failed to clear knowledge graph", err);
      setError(
        err instanceof Error ? err.message : "Failed to clear knowledge graph",
      );
    } finally {
      setIsClearing(false);
    }
  };

  const handleExtract = async () => {
    if (!orgId.trim()) {
      setError("Org ID is required");
      return;
    }

    setIsExtracting(true);
    setError(null);
    setResult(null);

    try {
      const url = httpClient.constructUrl(
        `v1/knowledge-graph/orgs/${orgId}/extract`,
      );
      const response = await httpClient.post(url, {
        body: JSON.stringify({ max_threads: maxThreads }),
        headers: robotoHeaders({ orgId }),
        // Not idempotent: this enqueues a command that runs an LLM tool-use loop over up to
        // max_threads threads. The default retry policy would re-send on a transient 5xx that may
        // already have reached the queue, multiplying a costly backfill.
        idempotent: false,
      });
      const data = await response.json<ExtractionResult>();
      setResult(data);
      void fetchStats();
    } catch (err) {
      LoggerService.error("Failed to trigger extraction", err);
      setError(
        err instanceof Error ? err.message : "Failed to trigger extraction",
      );
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Knowledge Graph</h3>
      <p className={styles.description}>
        Backfill the organization&apos;s knowledge graph from past chats, and
        inspect what has been remembered. New chats are extracted automatically
        as they finish, and a daily job catches any from the last 24 hours that
        were missed &mdash; so this page is only needed to reach further back
        than that, such as indexing an organization&apos;s archive for the first
        time.
      </p>

      <div className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="kg-org-id">
            Organization ID
          </label>
          <input
            id="kg-org-id"
            className={styles.input}
            type="text"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder="og_..."
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="kg-max-threads">
            How many past chats to look back through
          </label>
          <input
            id="kg-max-threads"
            className={styles.input}
            type="number"
            value={maxThreads}
            onChange={(e) => setMaxThreads(parseInt(e.target.value, 10) || 20)}
            min={1}
            max={200}
          />
          <p className={styles.fieldHint}>
            Walks backwards from the most recent chat through this many, and
            extracts the ones not already in the graph. Chats already extracted
            still count towards the total. Each new extraction is an LLM run
            taking roughly 30&ndash;90 seconds, so a large number here is a
            correspondingly large cost.
          </p>
        </div>

        <div className={styles.actions}>
          <button
            className={styles.primaryButton}
            onClick={() => void handleExtract()}
            disabled={isExtracting || !orgId.trim()}
          >
            {isExtracting ? "Extracting..." : "Extract from past threads"}
          </button>
          <button
            className={styles.secondaryButton}
            onClick={() => void fetchStats()}
            disabled={isLoadingStats || !orgId.trim()}
          >
            {isLoadingStats ? "Loading..." : "Load stats"}
          </button>
          <button
            className={styles.dangerButton}
            onClick={() => void handleClear()}
            disabled={isClearing || !orgId.trim()}
          >
            {isClearing ? "Clearing..." : "Clear knowledge graph"}
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {result && (
        <div className={styles.resultCard}>
          <h4 className={styles.resultHeading}>Extraction job enqueued</h4>
          <dl className={styles.statsList}>
            <div className={styles.statItem}>
              <dt>Past chats to look back through</dt>
              <dd>{result.max_threads}</dd>
            </div>
          </dl>
          <p className={styles.description}>
            The extraction runs asynchronously in the background. Check graph
            stats after a few minutes to see results.
          </p>
        </div>
      )}

      {stats && (
        <div className={styles.resultCard}>
          <h4 className={styles.resultHeading}>
            Graph stats for {stats.org_id}
          </h4>
          <dl className={styles.statsList}>
            <div className={styles.statItem}>
              <dt>Total nodes</dt>
              <dd>{stats.total_nodes}</dd>
            </div>
            <div className={styles.statItem}>
              <dt>Total edges</dt>
              <dd>{stats.total_edges}</dd>
            </div>
            <div className={styles.statItem}>
              <dt>Memory nodes</dt>
              <dd>{stats.memory_nodes}</dd>
            </div>
          </dl>
          {Object.keys(stats.kinds).length > 0 && (
            <>
              <h4 className={styles.resultHeading}>Memory kinds</h4>
              <dl className={styles.statsList}>
                {Object.entries(stats.kinds).map(([kind, count]) => (
                  <div key={kind} className={styles.statItem}>
                    <dt>{kind}</dt>
                    <dd>{count}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </div>
      )}
    </div>
  );
}
